import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger, cn } from '@design-system';
import type { ReplyStatusInTest, TestOption, TestStep } from '../api/api';

const SHOWN = 5;
const percent = (value: number) => `${Math.round(value * 100)}%`;

const STATUS_WORDS: Record<ReplyStatusInTest, string> = { live: 'Live', draft: 'Draft', editing: 'Editing' };

function Row({ option, chosen }: { option: TestOption; chosen: boolean }) {
  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className={cn('min-w-0 truncate', chosen ? 'font-semibold text-zinc-900' : 'text-zinc-700')}>
          {option.label}
          {option.status && (
            <span className="ml-2 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[11px] font-semibold text-zinc-600">
              {STATUS_WORDS[option.status]}
            </span>
          )}
        </span>
        <span className={cn('shrink-0 tabular-nums', chosen ? 'font-semibold text-zinc-900' : 'text-zinc-600')}>
          {percent(option.probability)}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-zinc-100">
        <div
          className={cn('h-full rounded-full', chosen ? 'bg-[#5B3FD6]' : 'bg-zinc-300')}
          style={{ width: `${Math.max(1, Math.round(option.probability * 100))}%` }}
        />
      </div>
    </li>
  );
}

/**
 * One step of the choice: every option the Decisions API weighed, with its probability, the one it
 * picked, and whether it was sure enough for the choice to stand.
 */
export function ProbabilityBars(props: { title: string; step: TestStep | null; empty: string }) {
  const { step } = props;
  if (!step) {
    return (
      <div className="flex flex-col gap-1.5">
        <h3 className="text-sm font-semibold">{props.title}</h3>
        <p className="text-[13px] text-zinc-600">{props.empty}</p>
      </div>
    );
  }
  const rest = step.options.slice(SHOWN);

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{props.title}</h3>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[12px] font-semibold',
            step.sure ? 'bg-[#E7F6EC] text-[#1B6B3A]' : 'bg-[#FFF4E5] text-[#8A4B00]',
          )}
        >
          {step.sure ? 'Sure enough' : 'Not sure enough'}
        </span>
      </div>
      <ul className="flex flex-col gap-2.5">
        {step.options.slice(0, SHOWN).map(option => (
          <Row
            key={option.value}
            option={option}
            chosen={option.value === step.choice}
          />
        ))}
      </ul>
      {rest.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger className="group inline-flex items-center gap-1 text-[13px] font-medium text-[#4527B8]">
            Show the other {rest.length}
            <ChevronDown
              className="size-3.5 transition-transform group-data-[state=open]:rotate-180"
              aria-hidden
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ul className="mt-2.5 flex flex-col gap-2.5">
              {rest.map(option => (
                <Row
                  key={option.value}
                  option={option}
                  chosen={option.value === step.choice}
                />
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      )}
      {step.reaskedOptions && (
        <p className="text-[13px] text-zinc-600">
          It was a close call, so it was asked again among the top three:{' '}
          {step.reaskedOptions.map(option => `${option.label} ${percent(option.probability)}`).join(', ')}.
        </p>
      )}
    </div>
  );
}
